import React, {useState} from 'react';
import {X, ChevronLeft, ChevronRight, Camera, Eye} from 'lucide-react';
import {AppText} from '../ui/Typography';

interface ImageItem {
  id: number | string;
  imageUrl: string;
  description?: string;
  imageType?: string;
}

interface ImageViewerProps {
  images: ImageItem[];
  title?: string;
  emptyMessage?: string;
  showThumbnails?: boolean;
  thumbnailSize?: 'small' | 'medium' | 'large';
  onImageClick?: (image: ImageItem, index: number) => void;
  className?: string;
}

const ImageViewer: React.FC<ImageViewerProps> = ({
  images,
  title,
  emptyMessage = 'لا توجد صور متاحة',
  showThumbnails = true,
  thumbnailSize = 'medium',
  onImageClick,
  className = ''
}) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

  const sizeClasses = {
    small: 'w-12 h-12',
    medium: 'w-16 h-16',
    large: 'w-20 h-20'
  };

  const openLightbox = (index: number) => {
    setSelectedImageIndex(index);
    onImageClick?.(images[index], index);
  };

  const closeLightbox = () => {
    setSelectedImageIndex(null);
  };

  const goToPrevious = () => {
    if (selectedImageIndex !== null) {
      setSelectedImageIndex(selectedImageIndex > 0 ? selectedImageIndex - 1 : images.length - 1);
    }
  };

  const goToNext = () => {
    if (selectedImageIndex !== null) {
      setSelectedImageIndex(selectedImageIndex < images.length - 1 ? selectedImageIndex + 1 : 0);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {closeLightbox();}
    if (e.key === 'ArrowLeft') {goToPrevious();}
    if (e.key === 'ArrowRight') {goToNext();}
  };

  if (!images || images.length === 0) {
    return (
      <div className={`bg-gray-50 rounded-lg p-6 text-center ${className}`}>
        <Camera className="w-8 h-8 text-gray-400 mx-auto mb-2" />
        <AppText className="text-sm text-gray-500">{emptyMessage}</AppText>
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Title */}
      {title && (
        <div className="flex items-center gap-3">
          <Camera className="h-5 w-5 text-gray-600" />
          <AppText className="font-semibold text-lg">{title}</AppText>
        </div>
      )}

      {/* Main Image Display */}
      <div className="relative w-full bg-gray-50 rounded-lg flex items-center justify-center min-h-48">
        <img
          src={images[0]?.imageUrl}
          alt={images[0]?.description || 'صورة رئيسية'}
          className="max-h-48 max-w-full object-contain rounded-md cursor-pointer hover:opacity-90 transition-opacity"
          onClick={() => openLightbox(0)}
          onError={(e) => {
            const img = e.target as HTMLImageElement;
            img.style.display = 'none';
          }}
        />
        {images.length > 1 && (
          <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-sm">
            {1} / {images.length}
          </div>
        )}
      </div>

      {/* Thumbnail Images */}
      {showThumbnails && images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto">
          {images.map((image, index) => (
            <div key={image.id} className="relative group cursor-pointer flex-shrink-0">
              <img
                src={image.imageUrl}
                alt={image.description || `صورة ${index + 1}`}
                className={`${sizeClasses[thumbnailSize]} object-cover bg-white rounded cursor-pointer p-0.5 shadow-sm border border-gray-300 hover:shadow-lg transition-shadow`}
                onClick={() => openLightbox(index)}
                onError={(e) => {
                  const img = e.target as HTMLImageElement;
                  img.style.display = 'none';
                }}
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="bg-white bg-opacity-90 rounded-full p-1">
                    <Eye className="h-3 w-3 text-gray-700" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Image Info */}
      {images.length > 0 && (
        <div className="text-center">
          <AppText className="text-sm text-gray-600">
            {images.length} صورة متاحة
          </AppText>
        </div>
      )}

      {/* Lightbox Modal */}
      {selectedImageIndex !== null && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center"
          onClick={closeLightbox}
          onKeyDown={handleKeyDown}
          tabIndex={0}
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
          >
            <X className="w-8 h-8" />
          </button>

          {/* Navigation Buttons */}
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goToPrevious();
                }}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10"
              >
                <ChevronLeft className="w-8 h-8" />
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goToNext();
                }}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10"
              >
                <ChevronRight className="w-8 h-8" />
              </button>
            </>
          )}

          {/* Main Image */}
          <div className="max-w-4xl max-h-[80vh] mx-4">
            <img
              src={images[selectedImageIndex].imageUrl}
              alt={images[selectedImageIndex].description || `صورة ${selectedImageIndex + 1}`}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
              onError={(e) => {
                const img = e.target as HTMLImageElement;
                img.style.display = 'none';
              }}
            />
          </div>

          {/* Image Counter */}
          {images.length > 1 && (
            <div className="absolute bottom-4 left-4 right-4 bg-black bg-opacity-50 text-white p-4 rounded-lg">
              <AppText className="text-sm">
                {selectedImageIndex + 1} من {images.length}
              </AppText>
              {images[selectedImageIndex].description && (
                <AppText className="text-sm mt-1">
                  {images[selectedImageIndex].description}
                </AppText>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageViewer;
